/**
 * Grouped Floating Action Burst.
 *
 * Renders a main floating action button that expands a set of child
 * sub-FABs either as an adaptive arc (filling the available angular
 * space around the main FAB) or as a line (along the axis with the
 * most free space). When `FAB_BURST_LOCK_VERTICAL_DOWN` is enabled in
 * configuration, only vertical downward line layout is used at runtime.
 *
 * Behavior:
 *   - Short press toggles the burst open/closed.
 *   - Long-press switches the expansion mode (`onModeToggle`) AND
 *     opens the burst so the user can immediately see the new layout
 *     (mode toggle is skipped while vertical lock is active).
 *   - Sub-FABs ignore pointer events until the open animation finishes,
 *     so a second tap on mobile cannot hit a still-stacked child (e.g.
 *     Pricing) while items animate out from the main FAB.
 *   - Outside click + Escape dismiss the burst.
 *   - A visible search autocomplete dropdown fades and auto-closes
 *     the burst to avoid overlapping the dropdown.
 *   - Honours `prefers-reduced-motion`.
 *
 * The component only owns measurement, state and JSX. All geometry
 * lives in `FloatingActionBurst/floatingActionBurstGeometry`, the long-press gesture in
 * `FloatingActionBurst/useBurstLongPress.hook`, the outside-click / Escape dismissal in
 * `useDismissOnOutsideInteraction`, the reduced-motion preference in
 * `useReducedMotion`, FAB sizes from `floatingAction.configuration`,
 * burst-specific constants from
 * `FloatingActionBurst/floatingActionBurst.configuration`, and the context in
 * `FloatingActionBurst/FloatingActionBurstContext`.
 */

import { IonIcon } from "@ionic/react";
import { apps, close } from "ionicons/icons";
import React, {
    Children,
    cloneElement,
    isValidElement,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
} from "react";
import { useDismissOnOutsideInteraction } from "../../hooks/useDismissOnOutsideInteraction.hook";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { MINI_MODAL_ANCHORED_OVERLAY_SELECTOR } from "../MiniModal/miniModal.overlay.configuration";
import {
    PRIMARY_FAB_SIZE_PX,
    SEARCH_AUTOCOMPLETE_PRIMARY_FAB_FADE_MS,
    SEARCH_AUTOCOMPLETE_PRIMARY_FAB_OPACITY,
} from "./floatingAction.configuration";
import {
    DEFAULT_LINE_SPACING_PX,
    DEFAULT_LONG_PRESS_MS,
    DEFAULT_SAFE_MARGIN_BOTTOM_PX,
    FAB_BURST_LOCK_VERTICAL_DOWN,
    FALLBACK_TOP_PX,
    LONG_PRESS_MOVE_TOLERANCE_PX,
    MIN_ITEM_ANGULAR_DENSITY_DEG,
} from "./FloatingActionBurst/floatingActionBurst.configuration";
import {
    FloatingActionBurstContext,
    type FloatingActionBurstContextValue,
} from "./FloatingActionBurst/FloatingActionBurstContext";
import {
    buildArcSvgPath,
    computeArcLayout,
    computeLineLayout,
    type ComputedLayout,
    type DirectionClearances,
    type FabExpansionMode,
} from "./FloatingActionBurst/floatingActionBurstGeometry";
import { useBurstLongPress } from "./FloatingActionBurst/useBurstLongPress.hook";

/** CSS selectors whose matches should NEVER trigger an outside-click close. */
const OUTSIDE_CLICK_IGNORE_SELECTORS: readonly string[] = [
    "[data-mini-modal=\"true\"]",
    MINI_MODAL_ANCHORED_OVERLAY_SELECTOR,
];

/** Duration (ms) of the child-item translate animation. */
const ITEM_TRANSFORM_DURATION_MS = 320;

/** Duration (ms) of the child-item opacity animation. */
const ITEM_OPACITY_DURATION_MS = 240;

/** Stagger step (ms) between sibling child openings. */
const STAGGER_OPEN_STEP_MS = 40;

/** Stagger step (ms) between sibling child closings. */
const STAGGER_CLOSE_STEP_MS = 25;

/** Duration (ms) of the SVG arc trace reveal. */
const ARC_TRACE_DURATION_MS = 420;

/** Scale applied to child items while the burst is collapsed. */
const ITEM_SCALE_WHEN_HIDDEN = 0.2;

/** Burst stroke opacity for the arc trace (subtle guide line). */
const ARC_TRACE_STROKE_OPACITY = 0.25;

/** Burst stroke width (px) for the arc trace. */
const ARC_TRACE_STROKE_WIDTH_PX = 2;

/** Snapshot of the main FAB on-screen metrics, recomputed on measurement events. */
interface FabMetrics {

    /** X coordinate (px) of the FAB center in the viewport. */
    "centerX": number;

    /** Y coordinate (px) of the FAB center in the viewport. */
    "centerY": number;

    /** Current viewport width (px). */
    "viewportWidth": number;

    /** Current viewport height (px). */
    "viewportHeight": number;
}

/** Public props accepted by the grouped floating action burst. */
export interface FloatingActionBurstProps {

    /** The child FAB nodes to lay out around the main FAB. */
    "children": React.ReactNode;

    /** Enables dark palette variants for the main FAB background. */
    "isDarkUi": boolean;

    /** Current expansion mode ("arc" or "line"). */
    "mode": FabExpansionMode;

    /** Called when the user requests a mode toggle via long-press. */
    "onModeToggle": () => void;

    /** Whether the burst is currently expanded (controlled by parent). */
    "isOpen": boolean;

    /** Called to propagate open/close requests to the parent. */
    "onOpenChange": ( isOpen: boolean ) => void;

    /** Whether a search autocomplete panel is currently open. */
    "isSearchAutocompleteOpen"?: boolean;

    /** Top safe-area (px). Defaults to `FALLBACK_TOP_PX`. */
    "safeMarginTopPx"?: number;

    /** Bottom safe-area (px). Defaults to `DEFAULT_SAFE_MARGIN_BOTTOM_PX`. */
    "safeMarginBottomPx"?: number;

    /** Accessible label applied to the main FAB and toolbar container. */
    "ariaLabel"?: string;

    /** Pixel spacing between successive items in line mode. Defaults to `DEFAULT_LINE_SPACING_PX`. */
    "itemSpacingPx"?: number;

    /** Long-press duration threshold (ms). Defaults to `DEFAULT_LONG_PRESS_MS`. */
    "longPressMs"?: number;
}

/**
 * FloatingActionBurst component — see the file header for the full
 * behavior specification.
 *
 * @param props Component props. See `FloatingActionBurstProps`.
 * @returns A React element rendering the main FAB and its animated
 *          children.
 */
const FloatingActionBurst: React.FC<FloatingActionBurstProps> = ( {
    children,
    isDarkUi,
    mode,
    onModeToggle,
    isOpen,
    onOpenChange,
    isSearchAutocompleteOpen = false,
    safeMarginTopPx = FALLBACK_TOP_PX,
    safeMarginBottomPx = DEFAULT_SAFE_MARGIN_BOTTOM_PX,
    ariaLabel = "Menu",
    itemSpacingPx = DEFAULT_LINE_SPACING_PX,
    longPressMs = DEFAULT_LONG_PRESS_MS,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const reducedMotion = useReducedMotion();

    const childArray = useMemo( () => {
        return Children.toArray( children ).filter( isValidElement );
    }, [ children ] );
    const childrenCount = childArray.length;

    const containerRef = useRef<HTMLDivElement | null>( null );
    const fabRef = useRef<HTMLButtonElement | null>( null );
    const panelCloseHandlersRef = useRef<Set<() => void>>( new Set() );
    const [ fabMetrics, setFabMetrics ] = useState<FabMetrics | null>( null );

    /**
     * When `true`, open sub-FAB wrappers accept pointer events. Stays
     * `false` for the full open animation so a second mobile tap cannot
     * hit a child still stacked on the main FAB. Closing clears the flag
     * via the unlock effect cleanup; `isVisible` also gates hits while
     * collapsed.
     */
    const [ areSubFabsInteractive, setAreSubFabsInteractive ] = useState( false );

    /**
     * Refresh the stored FAB metrics from DOM measurements. Called on
     * mount, on window resize, whenever the burst opens and whenever the
     * main FAB size changes (via `ResizeObserver`).
     */
    const refreshFabMetrics = useCallback( (): void => {
        const fabElement = fabRef.current;
        if ( fabElement === null ) {
            return;
        }
        const rect = fabElement.getBoundingClientRect();
        setFabMetrics( {
            "centerX":        rect.left + rect.width / 2,
            "centerY":        rect.top + rect.height / 2,
            "viewportWidth":  window.innerWidth,
            "viewportHeight": window.innerHeight,
        } );
    }, [] );

    useLayoutEffect( () => {
        refreshFabMetrics();
    }, [ refreshFabMetrics ] );

    useEffect( () => {
        /**
         * Re-measure on window resizes and on DOM-level changes of the
         * main FAB (responsive layouts, scrollbar-induced reflow, etc).
         */
        const handleResize = (): void => {
            refreshFabMetrics();
        };
        window.addEventListener( "resize", handleResize );

        let resizeObserver: ResizeObserver | null = null;
        if ( fabRef.current !== null && typeof ResizeObserver !== "undefined" ) {
            resizeObserver = new ResizeObserver( () => {
                refreshFabMetrics();
            } );
            resizeObserver.observe( fabRef.current );
        }

        return () => {
            window.removeEventListener( "resize", handleResize );
            if ( resizeObserver !== null ) {
                resizeObserver.disconnect();
            }
        };
    }, [ refreshFabMetrics ] );

    useEffect( () => {
        if ( isOpen ) {
            refreshFabMetrics();
        }
    }, [ isOpen, refreshFabMetrics ] );

    /*
     * Unlock sub-FAB pointer events after the full open animation (last
     * stagger delay + transform duration). Reduced motion unlocks on the
     * next tick with a zero delay. Cleanup clears the flag so a reopen
     * never inherits a stale interactive state; setState is only called
     * from the timer callback or effect cleanup, not synchronously in the
     * effect body.
     */
    useEffect( () => {
        if ( !isOpen ) {
            return;
        }

        const openInteractionLockMs = reducedMotion
            ? 0
            : Math.max( 0, childrenCount - 1 ) * STAGGER_OPEN_STEP_MS + ITEM_TRANSFORM_DURATION_MS;
        const unlockTimerId = window.setTimeout( () => {
            setAreSubFabsInteractive( true );
        }, openInteractionLockMs );

        return (): void => {
            window.clearTimeout( unlockTimerId );
            setAreSubFabsInteractive( false );
        };
    }, [ childrenCount, isOpen, reducedMotion ] );

    /*
     * Derive directional clearances from the current metrics. Each
     * clearance represents the pixel distance from the FAB center to the
     * respective edge of the usable viewport area.
     */
    const clearances = useMemo<DirectionClearances>( () => {
        if ( fabMetrics === null ) {
            return {
                "left":  0,
                "right": 0,
                "up":    0,
                "down":  0,
            };
        }

        return {
            "left":  fabMetrics.centerX,
            "right": fabMetrics.viewportWidth - fabMetrics.centerX,
            "up":    fabMetrics.centerY - safeMarginTopPx,
            "down":  fabMetrics.viewportHeight - fabMetrics.centerY - safeMarginBottomPx,
        };
    }, [ fabMetrics, safeMarginBottomPx, safeMarginTopPx ] );

    const layout = useMemo<ComputedLayout>( () => {
        if ( fabMetrics === null || childrenCount === 0 ) {
            return {
                "effectiveMode": mode,
                "offsets":       [],
                "arcRadiusPx":   0,
                "arcStartDeg":   0,
                "arcLengthDeg":  0,
            };
        }

        if ( FAB_BURST_LOCK_VERTICAL_DOWN ) {
            return computeLineLayout( clearances, childrenCount, itemSpacingPx, "down" );
        }

        if ( mode === "arc" ) {
            const arcLayout = computeArcLayout(
                fabMetrics.centerX,
                fabMetrics.centerY,
                fabMetrics.viewportWidth,
                fabMetrics.viewportHeight,
                safeMarginTopPx,
                safeMarginBottomPx,
                childrenCount,
                clearances,
            );
            if ( arcLayout !== null ) {
                const density = childrenCount > 0 ? arcLayout.arcLengthDeg / childrenCount : 0;
                if ( density >= MIN_ITEM_ANGULAR_DENSITY_DEG ) {
                    return arcLayout;
                }

                /*
                 * Items are too crowded to remain readable on the arc:
                 * fall back to a line layout for THIS render only,
                 * without changing the persisted user preference.
                 */
            }
        }

        return computeLineLayout( clearances, childrenCount, itemSpacingPx );
    }, [ childrenCount, clearances, fabMetrics, itemSpacingPx, mode, safeMarginBottomPx, safeMarginTopPx ] );

    /**
     * Registers a modal sub-FAB close handler while its panel is mounted.
     *
     * @param close Callback that closes the panel.
     * @returns Cleanup that removes the handler from the registry.
     */
    const registerPanelClose = useCallback( ( close: () => void ): () => void => {
        panelCloseHandlersRef.current.add( close );

        return (): void => {
            panelCloseHandlersRef.current.delete( close );
        };
    }, [] );

    /** Closes every open modal sub-FAB before the burst main FAB toggles. */
    const requestCloseOpenPanels = useCallback( (): void => {
        panelCloseHandlersRef.current.forEach( ( closePanel ) => {
            closePanel();
        } );
    }, [] );

    /**
     * Short press: dismiss any open sub-FAB MiniModal, then flip the burst
     * open/closed state without touching the persisted expansion mode.
     */
    const handleShortPress = useCallback( (): void => {
        requestCloseOpenPanels();
        onOpenChange( !isOpen );
    }, [ isOpen, onOpenChange, requestCloseOpenPanels ] );

    /**
     * Long press: dismiss open panels, toggle the expansion mode and force
     * the burst open so the user immediately sees the new layout. Mode toggle
     * is skipped while `FAB_BURST_LOCK_VERTICAL_DOWN` is active.
     */
    const handleLongPress = useCallback( (): void => {
        requestCloseOpenPanels();
        if ( !FAB_BURST_LOCK_VERTICAL_DOWN ) {
            onModeToggle();
        }
        onOpenChange( true );
    }, [ onModeToggle, onOpenChange, requestCloseOpenPanels ] );

    const longPressHandlers = useBurstLongPress( {
        "thresholdMs":       longPressMs,
        "moveTolerancePx":   LONG_PRESS_MOVE_TOLERANCE_PX,
        "onLongPressFired":  handleLongPress,
        "onShortPressFired": handleShortPress,
    } );

    /** Imperative close handler exposed to descendants through the context. */
    const requestBurstClose = useCallback( (): void => {
        onOpenChange( false );
    }, [ onOpenChange ] );

    useDismissOnOutsideInteraction( {
        "isActive":        isOpen,
        "containerRef":    containerRef,
        "onDismiss":       requestBurstClose,
        "ignoreSelectors": OUTSIDE_CLICK_IGNORE_SELECTORS,
    } );

    /*
     * Close automatically when the search autocomplete opens to avoid
     * visual overlap with the dropdown. The burst also fades out via
     * CSS (see `containerStyle` below).
     */
    useEffect( () => {
        if ( isSearchAutocompleteOpen && isOpen ) {
            onOpenChange( false );
        }
    }, [ isSearchAutocompleteOpen, isOpen, onOpenChange ] );

    /*
     * Animation timings. Reduced motion collapses everything to zero so
     * the UI remains perfectly usable for users who asked for it.
     */
    const itemTransformDurationMs = reducedMotion ? 0 : ITEM_TRANSFORM_DURATION_MS;
    const itemOpacityDurationMs = reducedMotion ? 0 : ITEM_OPACITY_DURATION_MS;
    const staggerOpenStepMs = reducedMotion ? 0 : STAGGER_OPEN_STEP_MS;
    const staggerCloseStepMs = reducedMotion ? 0 : STAGGER_CLOSE_STEP_MS;
    const arcTraceDurationMs = reducedMotion ? 0 : ARC_TRACE_DURATION_MS;
    const autocompleteFadeMs = reducedMotion ? 0 : SEARCH_AUTOCOMPLETE_PRIMARY_FAB_FADE_MS;

    const hasMeasuredFab = fabMetrics !== null;
    const showArcTrace = !FAB_BURST_LOCK_VERTICAL_DOWN &&
      isOpen &&
      layout.effectiveMode === "arc" &&
      layout.arcRadiusPx > 0 &&
      hasMeasuredFab;
    const arcCircumference = 2 * Math.PI * layout.arcRadiusPx * ( layout.arcLengthDeg / 360 );
    const arcSvgPath = layout.arcRadiusPx > 0
        ? buildArcSvgPath( layout.arcRadiusPx, layout.arcStartDeg, layout.arcLengthDeg )
        : "";

    const containerStyle: CSSProperties = {
        "opacity":       isSearchAutocompleteOpen ? SEARCH_AUTOCOMPLETE_PRIMARY_FAB_OPACITY : 1,
        "pointerEvents": isSearchAutocompleteOpen ? "none" : "auto",
        "transition":    `opacity ${ autocompleteFadeMs }ms ease-out`,
    };

    const mainFabStyle: CSSProperties = {
        "backgroundColor":         palette.floatingActionButtonBackground,
        "borderRadius":            "50%",
        "width":                   `${ PRIMARY_FAB_SIZE_PX }px`,
        "height":                  `${ PRIMARY_FAB_SIZE_PX }px`,
        "touchAction":             "manipulation",
        "userSelect":              "none",
        "WebkitUserSelect":        "none",
        "WebkitTapHighlightColor": "transparent",
    };

    /**
     * Inline style for a given child item. Absolute positioning + stagger
     * animation for the translate and opacity.
     *
     * @param index Child index in the parent-provided children array.
     * @returns CSS properties describing position + animation.
     */
    const getItemStyle = ( index: number ): CSSProperties => {
        const offset = layout.offsets[index] ?? {
            "dx": 0,
            "dy": 0,
        };
        const isVisible = isOpen && hasMeasuredFab;
        const translateX = isVisible ? offset.dx : 0;
        const translateY = isVisible ? offset.dy : 0;
        const scale = isVisible ? 1 : ITEM_SCALE_WHEN_HIDDEN;
        const delayMs = isVisible
            ? index * staggerOpenStepMs
            : ( childrenCount - 1 - index ) * staggerCloseStepMs;

        return {
            "position":      "absolute",
            "left":          "50%",
            "top":           "50%",
            "transform":     `translate(calc(-50% + ${ translateX }px), calc(-50% + ${ translateY }px)) scale(${ scale })`,
            "opacity":       isVisible ? 1 : 0,
            "transition":    `transform ${ itemTransformDurationMs }ms cubic-bezier(0.2, 0.8, 0.2, 1) ${ delayMs }ms, opacity ${ itemOpacityDurationMs }ms ease-out ${ delayMs }ms`,
            "pointerEvents": isVisible && areSubFabsInteractive ? "auto" : "none",
            "willChange":    "transform, opacity",
            "zIndex":        10,
        };
    };

    /*
     * Context value exposed to descendants. `mainFabRef` never changes
     * identity and `requestBurstClose` is memoized on its dependency,
     * so consumer re-renders only happen when they genuinely need to.
     */
    const burstContextValue = useMemo<FloatingActionBurstContextValue>( () => ( {
        "mainFabRef":             fabRef,
        "requestBurstClose":      requestBurstClose,
        "registerPanelClose":     registerPanelClose,
        "requestCloseOpenPanels": requestCloseOpenPanels,
    } ), [ registerPanelClose, requestBurstClose, requestCloseOpenPanels ] );

    return (
        <FloatingActionBurstContext.Provider value={burstContextValue}>
            <div
                aria-label={ariaLabel}
                className="relative"
                data-fab-burst="true"
                ref={containerRef}
                role="toolbar"
                style={containerStyle}
            >
                {/*
                  * SVG arc trace behind the items. Centered on the main FAB
                  * via a centered viewBox, reveals on open and hides on
                  * close by animating `stroke-dashoffset` + `stroke-opacity`.
                  */}
                {layout.arcRadiusPx > 0 && (
                    <svg
                        aria-hidden="true"
                        height={2 * layout.arcRadiusPx}
                        style={{
                            "position":      "absolute",
                            "left":          `calc(50% - ${ layout.arcRadiusPx }px)`,
                            "top":           `calc(50% - ${ layout.arcRadiusPx }px)`,
                            "pointerEvents": "none",
                        }}
                        viewBox={`-${ layout.arcRadiusPx } -${ layout.arcRadiusPx } ${ 2 * layout.arcRadiusPx } ${ 2 * layout.arcRadiusPx }`}
                        width={2 * layout.arcRadiusPx}
                    >
                        <path
                            d={arcSvgPath}
                            fill="none"
                            stroke={palette.overlayBorderStrong}
                            strokeDasharray={arcCircumference}
                            strokeDashoffset={showArcTrace ? 0 : arcCircumference}
                            strokeLinecap="round"
                            strokeOpacity={showArcTrace ? ARC_TRACE_STROKE_OPACITY : 0}
                            strokeWidth={ARC_TRACE_STROKE_WIDTH_PX}
                            style={{ "transition": `stroke-dashoffset ${ arcTraceDurationMs }ms cubic-bezier(0.2, 0.8, 0.2, 1), stroke-opacity ${ arcTraceDurationMs }ms ease-out` }}
                        />
                    </svg>
                )}
                {/*
                  * Child FABs. Each is wrapped in an absolutely-positioned
                  * div so the burst controls position/animation without
                  * changing the visual representation of the child itself.
                  */}
                {childArray.map( ( child, index ) => {
                    const reactChild = child as React.ReactElement;
                    const keyFromChild = reactChild.key;
                    const wrapperKey = keyFromChild !== null && keyFromChild !== undefined
                        ? String( keyFromChild )
                        : `fab-${ index }`;

                    return (
                        <div
                            key={wrapperKey}
                            style={getItemStyle( index )}
                        >
                            {cloneElement( reactChild )}
                        </div>
                    );
                } )}
                {/*
                  * Main FAB. Positioned in the normal flow so the parent
                  * can place the whole burst via regular margin/flex rules.
                  */}
                <button
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    aria-label={ariaLabel}
                    className="relative cursor-pointer flex items-center justify-center shadow-xl"
                    ref={fabRef}
                    style={mainFabStyle}
                    type="button"
                    {...longPressHandlers}
                >
                    {/*
                      * Two stacked icons cross-faded via opacity + rotation
                      * so the transition between open and closed looks
                      * continuous.
                      */}
                    <IonIcon
                        icon={apps}
                        style={{
                            "position":   "absolute",
                            "fontSize":   "20px",
                            "color":      palette.brandPrimary,
                            "opacity":    isOpen ? 0 : 1,
                            "transform":  isOpen ? "rotate(45deg) scale(0.8)" : "rotate(0deg) scale(1)",
                            "transition": reducedMotion
                                ? "none"
                                : "opacity 200ms ease-out, transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1)",
                        }}
                    />
                    <IonIcon
                        icon={close}
                        style={{
                            "position":   "absolute",
                            "fontSize":   "22px",
                            "color":      palette.brandPrimary,
                            "opacity":    isOpen ? 1 : 0,
                            "transform":  isOpen ? "rotate(0deg) scale(1)" : "rotate(-45deg) scale(0.8)",
                            "transition": reducedMotion
                                ? "none"
                                : "opacity 200ms ease-out, transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1)",
                        }}
                    />
                </button>
            </div>
        </FloatingActionBurstContext.Provider>
    );
};

export default FloatingActionBurst;
